import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, CreditCard, Package, CloudCog, FileDown } from "lucide-react";
import { api, downloadFile, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Billing() {
  const { user, setUser } = useAuth();
  const [pricing, setPricing] = useState(null);
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/api/compliance-os/pricing").then((r) => setPricing(r.data));
  }, []);

  const checkout = async (lookupKey) => {
    setBusy(lookupKey);
    setError("");
    try {
      const { data } = await api.post("/api/payments/checkout", { lookup_key: lookupKey, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      setError("Checkout could not start. Please try again.");
      setBusy("");
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel your MITIVISE Workspace subscription? You'll keep access until the end of the current billing period, then get a read-only grace period to export your data.")) return;
    setBusy("cancel");
    try {
      const { data } = await api.post("/api/app/billing/cancel");
      setMessage(data.message);
      setUser({ ...user, subscription_status: "active" });
    } catch (e) {
      setError(e.response?.data?.detail || "Cancellation could not be processed.");
    } finally {
      setBusy("");
    }
  };

  const active = user?.subscription_status === "active";

  return (
    <div className="mx-auto max-w-4xl space-y-8" data-testid="billing-page">
      <div>
        <p className="eyebrow">Billing & Plan</p>
        <h1 className="mt-3 font-serif text-4xl font-semibold text-navy">Your plan</h1>
      </div>

      <div className={`rounded-lg border p-7 ${active ? "border-gold bg-gold/5" : "border-navy/10 bg-white"}`} data-testid="billing-status">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-navy/50">Current status</p>
            <p className="mt-2 font-serif text-3xl font-semibold text-navy" data-testid="billing-status-text">
              {active ? "MITIVISE Workspace — Active" : user?.toolkit_owned ? "Toolkit owner" : "No purchase yet"}
            </p>
            {user?.toolkit_access_until && active && (
              <p className="mt-1 text-sm text-ink/60">Pro includes full toolkit access.</p>
            )}
            {!active && user?.toolkit_owned && (
              <p className="mt-1 text-sm text-ink/60">Toolkit access until {fmtDate(user?.toolkit_access_until)}.</p>
            )}
            {user?.subscription_status === "canceled" && (
              <p className="mt-1 text-sm text-ink/60">
                Subscription ended. Read-only access until {fmtDate(user?.grace_until)} — export your data below.
              </p>
            )}
            {user?.subscription_status === "past_due" && (
              <p className="mt-1 text-sm text-red-600">Your last payment failed. Update your payment method to restore full access.</p>
            )}
          </div>
          {active && (
            <button onClick={cancel} disabled={busy === "cancel"} data-testid="billing-cancel-button"
                    className="rounded-sm border border-red-200 px-5 py-3 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-60">
              {busy === "cancel" ? "Processing…" : "Cancel subscription"}
            </button>
          )}
        </div>
        {message && <p className="mt-4 rounded-sm border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800" data-testid="billing-message">{message}</p>}
        {error && <p className="mt-4 rounded-sm border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="billing-error">{error}</p>}
      </div>

      <div className="grid gap-5 md:grid-cols-2">
        <div className="flex h-full flex-col rounded-lg border border-navy/10 bg-white p-7" data-testid="billing-toolkit-card">
          <Package className="h-6 w-6 text-golddark" />
          <h2 className="mt-4 font-serif text-2xl font-semibold text-navy">MITIVISE Toolkit</h2>
          <p className="mt-1 font-mono text-sm font-semibold text-golddark">{pricing ? `$${pricing.toolkit_price.toFixed(0)} one-time` : "One-time purchase"}</p>
          <ul className="mt-4 flex-1 space-y-2 text-sm text-ink/70">
            <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-golddark" />Downloadable workbook, SOP pack, and checklist</li>
            <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-golddark" />Repeated downloads for a set window — files yours forever</li>
            <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-golddark" />Static product — no cloud monitoring or AI features</li>
          </ul>
          <button onClick={() => checkout("toolkit_onetime")} disabled={busy === "toolkit_onetime"} data-testid="billing-buy-toolkit"
                  className="btn-gold mt-6 w-full disabled:opacity-60">
            {busy === "toolkit_onetime" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
            Buy the Toolkit
          </button>
        </div>

        <div className="flex h-full flex-col rounded-lg border-2 border-gold bg-white p-7" data-testid="billing-pro-card">
          <CloudCog className="h-6 w-6 text-golddark" />
          <h2 className="mt-4 font-serif text-2xl font-semibold text-navy">MITIVISE Workspace</h2>
          <p className="mt-1 font-mono text-sm font-semibold text-golddark">{pricing ? `$${pricing.pro_monthly_price.toFixed(0)} / month` : "Monthly subscription"}</p>
          <ul className="mt-4 flex-1 space-y-2 text-sm text-ink/70">
            <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-golddark" />Everything in the toolkit, plus live cloud registers</li>
            <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-golddark" />AI-assisted SOP drafting, reminders, and reports</li>
            <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-golddark" />Direct line to TSM Group CLE services</li>
          </ul>
          <button onClick={() => checkout("pro_monthly")} disabled={busy === "pro_monthly"} data-testid="billing-subscribe"
                  className="btn-gold mt-6 w-full disabled:opacity-60">
            {busy === "pro_monthly" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
            {active ? "Manage in Stripe checkout" : "Subscribe Monthly"}
          </button>
        </div>
      </div>

      <div className="rounded-lg border border-navy/10 bg-white p-6" data-testid="billing-export">
        <h2 className="font-serif text-2xl font-semibold text-navy">Your data, always yours</h2>
        <p className="mt-2 text-sm text-ink/60">
          Export every register, SOP, and document record as JSON at any time. After a cancellation ends, a read-only grace
          period lets you export before account deletion under our published retention policy.
        </p>
        <button onClick={() => downloadFile("/api/app/export", "mitivise-export.json")} data-testid="billing-export-button"
                className="btn-outline mt-5 !py-2.5">
          <FileDown className="h-4 w-4" />
          Export my data
        </button>
      </div>

      <p className="text-xs italic leading-relaxed text-ink/45" data-testid="billing-disclaimer">
        Subscriptions renew monthly through Stripe until canceled. Prices are set by TSM Group CLE and can change with notice.
        Access to TSM professional services is not a substitute for legal advice, and service fees are separate from
        subscription charges unless a plan expressly includes them.
      </p>
    </div>
  );
}
